import { useCallback, useEffect, useMemo, useState } from "react";
import { usePracticeSessions, type PracticeSession } from "./usePracticeSessions";
import { useSongLibrary } from "./useSongLibrary";
import { useStudentAuth } from "./useStudentAuth";
import {
  loadActiveDays,
  loadCompletedTasks,
  loadFavorites,
  loadGymProgress,
  loadPracticeRecords,
  loadSongStudioSessions,
  loadStudentStats,
  getGymLevelSummaries,
  type SongStudioSessionRecord,
} from "../lib/bordonaProgressStorage";
import {
  badgeList,
  computeXp,
  levelFromXp,
  sessionTimestamp,
} from "../lib/dashboardGamification";
import { formatPracticeDuration } from "../lib/gym/progressMetrics";

export interface UnifiedSongActivity {
  id: string;
  songId: string;
  songTitle: string;
  mode: string;
  accuracyPct: number;
  subtitle: string;
  completedAt: number;
  source: "studio" | "songbook";
}

function reloadLocalSnapshot() {
  return {
    gym: loadGymProgress(),
    favorites: loadFavorites(),
    stats: loadStudentStats(),
    tasks: loadCompletedTasks(),
    practiceRecords: loadPracticeRecords(),
    activeDays: loadActiveDays(),
    songStudio: loadSongStudioSessions(),
  };
}

export function useBordonaDashboard() {
  const { profile } = useStudentAuth();
  const { sessions: firestoreSessions, loading: sessionsLoading } = usePracticeSessions();
  const { items: songs, loading: songsLoading } = useSongLibrary();
  const [local, setLocal] = useState(reloadLocalSnapshot);

  const refresh = useCallback(() => {
    setLocal(reloadLocalSnapshot());
  }, []);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key?.startsWith("bordona-")) refresh();
    };
    window.addEventListener("storage", onStorage);
    const id = window.setInterval(refresh, 4000);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.clearInterval(id);
    };
  }, [refresh]);

  const gymLevels = useMemo(() => getGymLevelSummaries(local.gym), [local.gym]);

  const xp = useMemo(
    () =>
      computeXp({
        gym: local.gym,
        tasks: local.tasks,
        songStudio: local.songStudio,
        firestoreSessions,
        stats: local.stats,
      }),
    [local, firestoreSessions]
  );

  const levelInfo = useMemo(() => levelFromXp(xp.total), [xp.total]);

  const badges = useMemo(
    () =>
      badgeList({
        gym: local.gym,
        tasks: local.tasks,
        favorites: local.favorites,
        stats: local.stats,
        songCount: songs.length,
      }),
    [local, songs.length]
  );

  const songActivity = useMemo((): UnifiedSongActivity[] => {
    const fromStudio: UnifiedSongActivity[] = local.songStudio.map((s) => ({
      id: s.id,
      songId: s.songId,
      songTitle: s.songTitle,
      mode: s.mode === "learn" ? "Aprender" : "Practicar",
      accuracyPct: Math.round(s.accuracy * 100),
      subtitle: `${s.hit + s.late}/${s.total} notas`,
      completedAt: s.completedAt,
      source: "studio",
    }));

    const fromFs: UnifiedSongActivity[] = firestoreSessions.map((s: PracticeSession) => {
      const acc =
        s.attempts > 0 ? Math.round((s.score / s.attempts) * 100) : s.flowScore;
      return {
        id: s.id,
        songId: s.songId,
        songTitle: s.songTitle,
        mode: s.mode === "learn" ? "Aprender" : "Reproducir",
        accuracyPct: acc,
        subtitle: `${Math.floor(s.duration / 60)}m · fluidez ${s.flowScore}%`,
        completedAt: sessionTimestamp(s.createdAt),
        source: "songbook",
      };
    });

    return [...fromStudio, ...fromFs].sort((a, b) => b.completedAt - a.completedAt);
  }, [local.songStudio, firestoreSessions]);

  const lastSong = songActivity[0] ?? null;

  const favoritesByType = useMemo(() => {
    const scales = local.favorites.filter((f) => f.type === "scale");
    const chords = local.favorites.filter((f) => f.type === "chord");
    const modes = local.favorites.filter((f) => f.type === "mode");
    return { scales, chords, modes, all: local.favorites };
  }, [local.favorites]);

  const tasksByType = useMemo(() => {
    const scaleTasks = local.tasks.filter((t) => t.type === "scale");
    const chordTasks = local.tasks.filter((t) => t.type === "chord" || t.type === "mode");
    return { scaleTasks, chordTasks, all: local.tasks };
  }, [local.tasks]);

  const heatmapDays = useMemo(() => {
    const set = new Set(local.activeDays);
    const today = new Date();
    const days: { date: string; active: boolean }[] = [];
    for (let i = 27; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const key = d.toISOString().split("T")[0];
      days.push({ date: key, active: set.has(key) });
    }
    return days;
  }, [local.activeDays]);

  const streak = Math.max(
    local.stats.currentStreak,
    local.gym.streakDays,
    0
  );

  return {
    profile,
    loading: sessionsLoading || songsLoading,
    refresh,
    gym: local.gym,
    gymLevels,
    stats: local.stats,
    practiceRecords: local.practiceRecords,
    favoritesByType,
    tasksByType,
    songActivity,
    lastSong,
    songsInLibrary: songs.length,
    xp,
    levelInfo,
    badges,
    heatmapDays,
    streak,
    gymTimeLabel: formatPracticeDuration(local.gym.totalPracticeTime),
    totalPracticeMinutes: local.stats.totalPracticeMinutes,
  };
}

export type { SongStudioSessionRecord };
